/**
 * FachbereicheManagement Component
 * 
 * Admin-only component for managing Fachbereiche (departments).
 * Allows creating, editing, and deleting Fachbereiche.
 * Name changes cascade to all related data.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useFachbereiche } from '../contexts/FachbereicheContext';
import { useAuth } from '../contexts/AuthContext';
import './FachbereicheManagement.css';
import './Switch.css';

interface Fachbereich {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  betreuers_enabled?: number | boolean;
}

interface FachbereicheManagementProps {
  onClose: () => void;
  token: string;
}

const FachbereicheManagement: React.FC<FachbereicheManagementProps> = ({ onClose, token }) => {
  const { refreshFachbereiche } = useFachbereiche();
  const { year } = useAuth();
  const [fachbereiche, setFachbereiche] = useState<Fachbereich[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [newName, setNewName] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [openKebabMenuId, setOpenKebabMenuId] = useState<number | null>(null);
  const [yearMaintenanceDisabled, setYearMaintenanceDisabled] = useState<boolean>(false);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    fetchFachbereiche();
    fetchMaintenanceStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success && response.data.years && year) {
        const currentYearData = response.data.years.find((y: { year: number; disabled: boolean }) => y.year === year);
        if (currentYearData) {
          setYearMaintenanceDisabled(currentYearData.disabled || false);
        }
      }
    } catch (err) {
      console.error('Error fetching maintenance status:', err);
      // Default to false (not disabled) if fetch fails
      setYearMaintenanceDisabled(false);
    }
  };

  // Close kebab menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.fachbereiche-kebab') && !target.closest('.fachbereiche-kebab-menu')) {
        setOpenKebabMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchFachbereiche = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/fachbereiche`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setFachbereiche(response.data.fachbereiche || []);
      } else {
        setError(response.data.message || 'Fehler beim Laden der Fachbereiche');
      }
    } catch (err: any) {
      console.error('Error fetching Fachbereiche:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Fachbereiche');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/fachbereiche`,
        { name: newName.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setNewName('');
        await fetchFachbereiche();
        await refreshFachbereiche(); // Refresh global Fachbereiche list
      } else {
        setError(response.data.message || 'Fehler beim Erstellen des Fachbereichs');
      }
    } catch (err: any) {
      console.error('Error creating Fachbereich:', err);
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Fachbereichs');
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (fachbereich: Fachbereich) => {
    setEditingId(fachbereich.id);
    setEditingName(fachbereich.name);
    setOpenKebabMenuId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleSaveEdit = async (id: number) => {
    if (!editingName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      setError('');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/fachbereiche/${id}`,
        { name: editingName.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setEditingId(null);
        setEditingName('');
        await fetchFachbereiche();
        await refreshFachbereiche(); // Refresh global Fachbereiche list
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren des Fachbereichs');
      }
    } catch (err: any) {
      console.error('Error updating Fachbereich:', err);
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Fachbereichs');
    }
  };

  const handleToggleBetreuers = async (id: number, enabled: boolean) => {
    // Optimistic update: update UI immediately
    setFachbereiche(prevFachbereiche => 
      prevFachbereiche.map(fb => 
        fb.id === id 
          ? { ...fb, betreuers_enabled: enabled ? 1 : 0 }
          : fb
      )
    );

    try {
      setError('');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/fachbereiche/${id}`,
        { betreuers_enabled: enabled },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Update local state with server response instead of full refresh for better performance
        if (response.data.fachbereich) {
          setFachbereiche(prevFachbereiche => 
            prevFachbereiche.map(fb => 
              fb.id === id 
                ? { ...fb, betreuers_enabled: response.data.fachbereich.betreuers_enabled }
                : fb
            )
          );
        }
        // Refresh global cache in background (non-blocking) - instant UI update
        refreshFachbereiche();
      } else {
        // Revert optimistic update on error
        setFachbereiche(prevFachbereiche => 
          prevFachbereiche.map(fb => 
            fb.id === id 
              ? { ...fb, betreuers_enabled: enabled ? 0 : 1 }
              : fb
          )
        );
        setError(response.data.message || 'Fehler beim Aktualisieren der Betreuer-Einstellung');
      }
    } catch (err: any) {
      // Revert optimistic update on error
      setFachbereiche(prevFachbereiche => 
        prevFachbereiche.map(fb => 
          fb.id === id 
            ? { ...fb, betreuers_enabled: enabled ? 0 : 1 }
            : fb
        )
      );
      console.error('Error toggling betreuers_enabled:', err);
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Betreuer-Einstellung');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Möchten Sie diesen Fachbereich wirklich löschen? Alle zugeordneten Helfer, Benutzer und Positionen werden ebenfalls entfernt.')) {
      return;
    }

    try {
      setDeleting(id);
      setError('');
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/fachbereiche/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setOpenKebabMenuId(null);
        await fetchFachbereiche();
        await refreshFachbereiche(); // Refresh global Fachbereiche list
      } else {
        setError(response.data.message || 'Fehler beim Löschen des Fachbereichs');
      }
    } catch (err: any) {
      console.error('Error deleting Fachbereich:', err);
      setError(err.response?.data?.message || 'Fehler beim Löschen des Fachbereichs');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="fachbereiche-overlay" onClick={(e) => {
      // Only close if clicking directly on the overlay, not on child elements
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="fachbereiche-popup" onClick={(e) => e.stopPropagation()}>
        <div className="fachbereiche-header">
          <h2 className="fachbereiche-title">FACHBEREICHE VERWALTEN</h2>
          <button 
            className="fachbereiche-close-button" 
            onClick={(e) => { 
              e.preventDefault();
              e.stopPropagation(); 
              onClose(); 
            }} 
            title="Schließen"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="fachbereiche-content">
          {error && (
            <div className="fachbereiche-error">{error}</div>
          )}

          {yearMaintenanceDisabled && (
            <div className="fachbereiche-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px' }}>
              ⚠️ Das Jahr {year} ist schreibgeschützt. Fachbereiche können nicht erstellt, bearbeitet oder gelöscht werden.
            </div>
          )}
          <form onSubmit={handleCreate} className="fachbereiche-create-form">
            <div className="fachbereiche-form-group">
              <label htmlFor="new-fachbereich-name">Neuer Fachbereich:</label>
              <input
                type="text"
                id="new-fachbereich-name"
                name="new-fachbereich-name"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setError('');
                }}
                placeholder="Fachbereich-Name eingeben"
                disabled={creating || yearMaintenanceDisabled}
                required
                maxLength={100}
              />
            </div>
            <button
              type="submit"
              className="fachbereiche-create-button"
              disabled={creating || !newName.trim() || yearMaintenanceDisabled}
              title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
            >
              {creating ? 'Erstellt...' : 'Fachbereich erstellen'}
            </button>
          </form>

          {loading ? (
            <div className="fachbereiche-loading">Lädt...</div>
          ) : (
            <div className="fachbereiche-list-container">
              <table className="fachbereiche-table">
                <tbody>
                  {fachbereiche.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="fachbereiche-empty">
                        Noch keine Fachbereiche vorhanden
                      </td>
                    </tr>
                  ) : (
                    fachbereiche.map((fachbereich) => (
                      <tr key={fachbereich.id}>
                        <td data-label="Name">
                          {editingId === fachbereich.id ? (
                            <input
                              type="text"
                              id={`fachbereich-${fachbereich.id}`}
                              name={`fachbereich-${fachbereich.id}`}
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !yearMaintenanceDisabled) {
                                  handleSaveEdit(fachbereich.id);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              onBlur={() => {
                                if (!yearMaintenanceDisabled) {
                                  handleSaveEdit(fachbereich.id);
                                } else {
                                  handleCancelEdit();
                                }
                              }}
                              autoFocus
                              className="fachbereiche-edit-input"
                              maxLength={100}
                              disabled={yearMaintenanceDisabled}
                            />
                          ) : (
                            <span className="fachbereiche-name">{fachbereich.name}</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                              {fachbereich.betreuers_enabled !== undefined && fachbereich.betreuers_enabled !== 0 && fachbereich.betreuers_enabled !== false ? 'Betreuer sichtbar' : 'Betreuer versteckt'}
                            </span>
                            <label className={`switch-container ${editingId === fachbereich.id || yearMaintenanceDisabled ? 'disabled' : ''}`} style={{ opacity: editingId === fachbereich.id || yearMaintenanceDisabled ? 0.6 : 1 }}>
                              <input
                                type="checkbox"
                                className="switch-input"
                                checked={fachbereich.betreuers_enabled !== undefined && fachbereich.betreuers_enabled !== 0 && fachbereich.betreuers_enabled !== false}
                                onChange={(e) => {
                                  if (!yearMaintenanceDisabled) {
                                    const newValue = e.target.checked;
                                    handleToggleBetreuers(fachbereich.id, newValue);
                                  }
                                }}
                                disabled={editingId === fachbereich.id || yearMaintenanceDisabled}
                                title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : (fachbereich.betreuers_enabled !== undefined && fachbereich.betreuers_enabled !== 0 && fachbereich.betreuers_enabled !== false ? "Betreuer sichtbar" : "Betreuer versteckt")}
                              />
                              <span className="switch-track switch-track-red">
                                <span className="switch-thumb" />
                              </span>
                            </label>
                          </div>
                        </td>
                        <td className="fachbereiche-actions-cell">
                          {editingId === fachbereich.id ? (
                            <div className="fachbereiche-edit-actions">
                              <button
                                className="fachbereiche-save-button"
                                onClick={() => handleSaveEdit(fachbereich.id)}
                              >
                                Speichern
                              </button>
                              <button
                                className="fachbereiche-cancel-button"
                                onClick={handleCancelEdit}
                              >
                                Abbrechen
                              </button>
                            </div>
                          ) : (
                            <div style={{ position: 'relative' }}>
                              <button
                                className="fachbereiche-kebab"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenKebabMenuId(openKebabMenuId === fachbereich.id ? null : fachbereich.id);
                                }}
                                title="Optionen"
                              >
                                ⋮
                              </button>
                              {openKebabMenuId === fachbereich.id && (
                                <div className="fachbereiche-kebab-menu">
                                  <button
                                    className="fachbereiche-kebab-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenKebabMenuId(null);
                                      if (!yearMaintenanceDisabled) {
                                        handleStartEdit(fachbereich);
                                      }
                                    }}
                                    disabled={yearMaintenanceDisabled}
                                    title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                                  >
                                    Bearbeiten
                                  </button>
                                  <button
                                    className="fachbereiche-kebab-item fachbereiche-kebab-item-danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenKebabMenuId(null);
                                      if (!yearMaintenanceDisabled) {
                                        handleDelete(fachbereich.id);
                                      }
                                    }}
                                    disabled={deleting === fachbereich.id || yearMaintenanceDisabled}
                                    title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                                  >
                                    {deleting === fachbereich.id ? 'Löscht...' : 'Löschen'}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FachbereicheManagement;

